import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchChatFlags } from '@/api/featureFlags';

/** Map of feature flag names to boolean values. Default = true (fail-open) for unknown flags. */
export type FlagsMap = Record<string, boolean>;

export const FlagsContext = createContext<FlagsMap>({});

/**
 * Read a single boolean feature flag.
 * Returns `true` if the flag is absent from context (fail-open default).
 * The kill-switch `chat.multistream.enabled` should default to true so multistream is on by default.
 */
export function useFlag(flagName: string): boolean {
  const flags = useContext(FlagsContext);
  return flags[flagName] ?? true;
}

// ---------------------------------------------------------------------------
// Boot-time flag fetch (ADR #11: AppConfig fetched once into FlagsContext,
// localStorage cache for boot-time fallback).
//
// The chat kill-switch lives in the AppConfig `chat` profile, surfaced by the
// backend at `/api/feature-flags/chat` as `{ multistream: { enabled } }` (see
// WayneAIPlatformCDK Phase 5 - Task 19). We mirror the existing fullauto fetch
// pattern (`useFullAutoFlag` -> react-query) so multiple consumers share one
// network round-trip and the value is cached for the session.
//
// Fail-open: while the fetch is in flight, errors, or the key is missing, the
// flag resolves to `true` so beta/missing-config still gets multistream. The
// kill-switch only takes effect once AppConfig has explicitly published
// `multistream.enabled = false`.
// ---------------------------------------------------------------------------

const FLAGS_CACHE_KEY = 'wayne.v2.flags';

/** A small allowlist of flags persisted for boot-time fallback. No PII. */
type CachedFlags = Partial<Pick<FlagsMap, 'chat.multistream.enabled'>>;

function readCachedFlags(): CachedFlags {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(FLAGS_CACHE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      const v = (parsed as Record<string, unknown>)['chat.multistream.enabled'];
      if (typeof v === 'boolean') return { 'chat.multistream.enabled': v };
    }
    return {};
  } catch {
    return {};
  }
}

function writeCachedFlags(flags: CachedFlags): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(FLAGS_CACHE_KEY, JSON.stringify(flags));
  } catch {
    /* ignore — storage may be unavailable in some browser modes */
  }
}

/**
 * FlagsProvider — fetches AppConfig-backed flags at boot and exposes them to
 * `useFlag` consumers. Mount once in the provider tree (see `App.tsx`).
 *
 * Resolution order for `chat.multistream.enabled`:
 *   1. The live `/api/feature-flags/chat` response, once it lands.
 *   2. The localStorage cache from a prior session, while the fetch is in
 *      flight or after a transient error.
 *   3. Fail-open `true` (the `useFlag` default) when neither is available.
 */
export function FlagsProvider({ children }: { children: ReactNode }): JSX.Element {
  const query = useQuery({
    queryKey: ['feature-flags', 'chat'],
    queryFn: fetchChatFlags,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const flags = useMemo<FlagsMap>(() => {
    const next: FlagsMap = {};
    const cached = readCachedFlags();
    if (typeof cached['chat.multistream.enabled'] === 'boolean') {
      next['chat.multistream.enabled'] = cached['chat.multistream.enabled'];
    }
    if (query.data) {
      const enabled = query.data.multistream.enabled;
      next['chat.multistream.enabled'] = enabled;
      // Refresh the boot-time cache so the next cold start matches AppConfig.
      writeCachedFlags({ 'chat.multistream.enabled': enabled });
    }
    return next;
  }, [query.data]);

  return <FlagsContext.Provider value={flags}>{children}</FlagsContext.Provider>;
}
